import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  BASE_END_POINT_USER,
  JOB_END_POINT,
  USER_END_POINT
} from '../../../config';
import { messageUpdated } from '../app/appSlice';
import { Severity } from '../../../components/Message/Message';
import { loginSucceeded } from '../user/usersSlice';
import { getLocalStorageAuthentication } from '../../../lib/auth';

// Array of friendly, motivating welcome messages for companies
const COMPANY_WELCOME_MESSAGES = [
  '🎉 Welcome back! Ready to find your next rockstar team member?',
  '✨ Great to see you! Your next amazing hire is just a post away!',
  "🚀 Welcome aboard! Let's build something incredible together today!",
  '💼 Hey there! Time to connect with world-class remote talent!',
  "🌟 You're in! Let's turn your job opening into the perfect match!",
  '👋 Welcome back, superstar! Ready to grow your dream team?',
  '🎯 Logged in and ready to win! Post a job and watch the magic happen!',
  "💪 You're all set! Let's find exceptional talent for your company!",
  '🔥 Welcome back! Your future team member is out there waiting!',
  '⚡ Success! Time to supercharge your team with top remote talent!',
  '🌍 Hello again! Connect with brilliant minds from around the globe!',
  "🎊 You're back! Let's make today the day you find THE perfect candidate!",
  "💎 Welcome! Your company deserves the best—let's find them together!",
  "🏆 You're in! Champions hire champions. Ready to build your A-team?",
  '🎪 Great to see you! The remote talent show is waiting for your audition!',
  "🚢 All aboard! Let's navigate the sea of talent and find your treasure!",
  '🎨 Welcome back, creator! Time to paint your masterpiece team!',
  '🌈 Success! Your rainbow of remote talent opportunities starts now!',
  "🎸 You're plugged in! Let's compose your perfect team harmony!",
  '🔮 Welcome back, visionary! Your future hire is closer than you think!'
];

// Array of friendly, motivating welcome messages for candidates
const CANDIDATE_WELCOME_MESSAGES = [
  '🎉 Welcome back! Your dream remote job is waiting for you!',
  '✨ Great to see you! Today could be the day you land your perfect role!',
  "🚀 Welcome aboard! Let's launch your remote career to new heights!",
  '💼 Hey there, talent! Amazing opportunities are just a click away!',
  "🌟 You're in! Let's match your skills with the perfect remote opportunity!",
  '👋 Welcome back, champion! Ready to discover your next adventure?',
  '🎯 Logged in and ready to win! Your ideal job match is out there!',
  "💪 You're all set! Let's find the company that deserves your talent!",
  '🔥 Welcome back! Your future employer is searching for someone like you!',
  '⚡ Success! Time to supercharge your career with amazing remote opportunities!',
  '🌍 Hello again! Explore opportunities from companies around the globe!',
  "🎊 You're back! Let's make today the day you find THE perfect job!",
  "💎 Welcome! You deserve the best—let's find your dream role together!",
  "🏆 You're in! Top companies are looking for A-players like you!",
  '🎪 Great to see you! The world of remote work is your stage!',
  "🚢 All aboard! Let's navigate your journey to career success!",
  '🎨 Welcome back, creator! Time to paint your career masterpiece!',
  '🌈 Success! Your rainbow of remote opportunities starts now!',
  "🎸 You're plugged in! Let's find the job that hits all the right notes!",
  '🔮 Welcome back, rising star! Your next opportunity is closer than you think!'
];

// Function to get a random welcome message based on user role
const getRandomWelcomeMessage = (userRoles) => {
  const isCandidate = userRoles?.includes('ROLE_CANDIDATE');
  const messages = isCandidate
    ? CANDIDATE_WELCOME_MESSAGES
    : COMPANY_WELCOME_MESSAGES;
  const randomIndex = Math.floor(Math.random() * messages.length);
  return messages[randomIndex];
};

const trackActivationSuccess = (userRoles) => {
  if (typeof window === 'undefined') return;
  if (!window.dataLayer || !Array.isArray(window.dataLayer)) return;
  const isCandidate = userRoles?.includes('ROLE_CANDIDATE');
  const userType = isCandidate ? 'candidate' : 'company';
  window.dataLayer.push({
    event: 'activation_success',
    user_type: userType
  });
};

export const userApi = createApi({
  reducerPath: 'userApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${BASE_END_POINT_USER}`
  }),
  endpoints: (build) => ({
    registerUser: build.mutation({
      query: ({ fullName, email, password, username }) => {
        return {
          url: `${USER_END_POINT}/register`,
          method: 'POST',
          body: { fullName, email, password, username }
        };
      },
      // Effects to trigger on success
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        const errorMessage = (
          message = 'Registration failed, please try again'
        ) => {
          dispatch(
            messageUpdated({
              text: message,
              severity: Severity.ERROR
            })
          );
        };
        try {
          const { data } = await queryFulfilled;
          const isUserRegistered = data?.data?.id ?? false;

          // Check if there's an error message in the response (even with 200 status)
          const responseMessage = data?.message || '';
          if (!isUserRegistered && responseMessage) {
            // Backend returned an error message
            errorMessage(responseMessage);
            return;
          }
          // Check if a user is registered
          if (!isUserRegistered) {
            errorMessage('User not registered, please try again');
          }
        } catch (error) {
          // Check if the error message is about inactive account
          const errMsg = error?.data?.message || error?.message || '';
          if (errMsg.toLowerCase().includes("hasn't been activated")) {
            errorMessage(errMsg);
          } else {
            errorMessage();
          }
        }
      }
    }),
    loginUser: build.mutation({
      query: ({ email, password }) => {
        return {
          url: `${USER_END_POINT}/auth/token`,
          method: 'POST',
          body: { email, password }
        };
      },
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        const errorMessage = (message = 'Login failed, please try again') => {
          dispatch(
            messageUpdated({
              text: message,
              severity: Severity.ERROR
            })
          );
        };
        try {
          const { data } = await queryFulfilled;
          const isUserLoginSuccess = data?.data?.user?.id ?? false;

          // Check if account is not activated
          const errorMsg = data?.message || '';
          if (errorMsg.toLowerCase().includes('not activated')) {
            errorMessage(
              'Account not activated. Please check your email for the activation link.'
            );
            return;
          }

          if (isUserLoginSuccess) {
            // Normalize the user object to include userId and fullName fields
            const userData = data.data.user;
            const normalizedUser = {
              ...userData,
              userId: userData.id,
              fullName:
                userData.fullName || userData.full_name || userData.email
            };
            console.log('🔍 [Login] User data from API:', userData);
            console.log('✅ [Login] Normalized user:', normalizedUser);

            // Use random welcome message for successful login based on user role
            dispatch(
              messageUpdated({
                text: getRandomWelcomeMessage(normalizedUser.roles),
                severity: Severity.SUCCESS
              })
            );

            dispatch(
              loginSucceeded({
                token: userData.token,
                user: normalizedUser
              })
            );
          } else {
            errorMessage();
          }
        } catch (error) {
          const errMsg =
            error?.data?.message ||
            error?.message ||
            'Login failed, please try again';
          errorMessage(errMsg);
        }
      }
    }),
    updateUser: build.mutation({
      query: ({ user, JWT }) => {
        return {
          url: `${JOB_END_POINT}/${user.id}`,
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${JWT}`
          },
          body: user
        };
      }
    }),
    activateAccount: build.mutation({
      query: (token) => {
        return {
          url: `${USER_END_POINT}/activate?token=${token}`,
          method: 'GET'
        };
      },
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        try {
          const { data } = await queryFulfilled;
          const isActivated = data?.data?.user?.activated === true;
          if (isActivated) {
            trackActivationSuccess(data?.data?.user?.roles);
          }
          const text = isActivated
            ? 'Account activated successfully!'
            : 'Activation failed, please try again';
          dispatch(
            messageUpdated({
              text: text,
              severity: isActivated ? Severity.SUCCESS : Severity.ERROR
            })
          );
        } catch (error) {
          const errorMsg = error?.error?.data?.message || 'Activation failed';
          dispatch(
            messageUpdated({
              text: errorMsg,
              severity: Severity.ERROR
            })
          );
        }
      }
    }),
    activateAccountWithRecaptcha: build.mutation({
      query: ({ token, recaptchaToken }) => {
        return {
          url: `${USER_END_POINT}/activate`,
          method: 'POST',
          body: { token, recaptchaToken }
        };
      },
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        try {
          const { data } = await queryFulfilled;
          const isActivated = data?.data?.user?.activated === true;
          if (isActivated) {
            trackActivationSuccess(data?.data?.user?.roles);
          }
          const text = isActivated
            ? 'Account activated successfully!'
            : 'Activation failed, please try again';
          dispatch(
            messageUpdated({
              text: text,
              severity: isActivated ? Severity.SUCCESS : Severity.ERROR
            })
          );
        } catch (error) {
          const errorMsg = error?.error?.data?.message || 'Activation failed';
          dispatch(
            messageUpdated({
              text: errorMsg,
              severity: Severity.ERROR
            })
          );
        }
      }
    }),
    resendActivationEmail: build.mutation({
      query: (email) => {
        return {
          url: `${USER_END_POINT}/resend-activation`,
          method: 'POST',
          body: { email }
        };
      },
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        try {
          const { data } = await queryFulfilled;
          const message = data?.data?.message || 'Activation email sent!';
          dispatch(
            messageUpdated({
              text: message,
              severity: Severity.SUCCESS
            })
          );
        } catch (error) {
          const errorMsg =
            error?.error?.data?.message || 'Failed to resend activation email';
          dispatch(
            messageUpdated({
              text: errorMsg,
              severity: Severity.ERROR
            })
          );
        }
      }
    }),
    forgotPassword: build.mutation({
      query: ({ email, recaptchaToken }) => {
        return {
          url: `${USER_END_POINT}/forgot-password`,
          method: 'POST',
          body: { email, recaptchaToken }
        };
      },
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        try {
          const { data } = await queryFulfilled;
          const message =
            data?.data?.message ||
            'If an account with that email exists, you will receive a password reset link.';
          dispatch(
            messageUpdated({
              text: message,
              severity: Severity.SUCCESS
            })
          );
        } catch (error) {
          const errorMsg =
            error?.error?.data?.message ||
            'Failed to process password reset request';
          dispatch(
            messageUpdated({
              text: errorMsg,
              severity: Severity.ERROR
            })
          );
        }
      }
    }),
    resetPassword: build.mutation({
      query: ({ token, newPassword, recaptchaToken }) => {
        return {
          url: `${USER_END_POINT}/reset-password`,
          method: 'POST',
          body: { token, newPassword, recaptchaToken }
        };
      },
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        try {
          const { data } = await queryFulfilled;
          const message =
            data?.data?.message || 'Your password has been successfully reset';
          dispatch(
            messageUpdated({
              text: message,
              severity: Severity.SUCCESS
            })
          );
        } catch (error) {
          const errorMsg =
            error?.error?.data?.message || 'Failed to reset password';
          dispatch(
            messageUpdated({
              text: errorMsg,
              severity: Severity.ERROR
            })
          );
        }
      }
    }),
    changePassword: build.mutation({
      query: ({ currentPassword, newPassword, recaptchaToken }) => {
        const localAuth = getLocalStorageAuthentication();
        return {
          url: `${USER_END_POINT}/change-password`,
          method: 'POST',
          headers: {
            Authorization: `Bearer ${localAuth.token}`
          },
          body: { currentPassword, newPassword, recaptchaToken }
        };
      },
      onQueryStarted: async (arg, { queryFulfilled, dispatch }) => {
        try {
          const { data } = await queryFulfilled;
          const message =
            data?.data?.message ||
            'Your password has been successfully changed';
          dispatch(
            messageUpdated({
              text: message,
              severity: Severity.SUCCESS
            })
          );
        } catch (error) {
          const errorMsg =
            error?.error?.data?.message || 'Failed to change password';
          dispatch(
            messageUpdated({
              text: errorMsg,
              severity: Severity.ERROR
            })
          );
        }
      }
    })
  })
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useLoginUserMutation,
  useRegisterUserMutation,
  useUpdateUserMutation,
  useActivateAccountMutation,
  useActivateAccountWithRecaptchaMutation,
  useResendActivationEmailMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useChangePasswordMutation
} = userApi;
